import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  SimpleGrid,
  Text,
  Badge,
  VStack,
  HStack,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { Task } from '../api/taskApi';
import { useOrganizationEnums } from '../hooks/useOrganizationEnums';
import { useUpdateTask } from '../hooks/useTasks';
import { useAuthenticatedUser } from '../contexts/AuthenticatedUserInfoContext';
import { hasPermission, Permission } from '../utils/permissions';

interface TaskKanbanProps {
  tasks: Task[];
}

const columnId = (status: string) => `column:${status}`;

const parseColumnId = (id: string): string | null =>
  id.startsWith('column:') ? id.slice('column:'.length) : null;

interface KanbanCardProps {
  task: Task;
  canDrag: boolean;
  parentTitle?: string;
  onOpen: (taskId: string) => void;
}

const KanbanCard: React.FC<KanbanCardProps> = ({ task, canDrag, parentTitle, onOpen }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: task._id,
    disabled: !canDrag,
  });

  const style = transform
    ? { transform: CSS.Translate.toString(transform) }
    : undefined;

  return (
    <Box
      ref={setNodeRef}
      style={style}
      bg={cardBg}
      p={3}
      borderRadius="lg"
      boxShadow="sm"
      cursor={canDrag ? 'grab' : 'pointer'}
      opacity={isDragging ? 0.4 : 1}
      _hover={{ boxShadow: 'md' }}
      _active={canDrag ? { cursor: 'grabbing' } : undefined}
      onClick={() => onOpen(task._id)}
      {...(canDrag ? { ...attributes, ...listeners } : {})}
      borderLeft={task.isSubtask || task.parentTask ? '3px solid' : undefined}
      borderLeftColor={task.isSubtask || task.parentTask ? 'blue.400' : undefined}
    >
      {(task.isSubtask || task.parentTask) && (
        <Badge mb={1} size="sm" colorScheme="purple" variant="subtle">
          Subtask
        </Badge>
      )}
      <Text fontSize="sm" fontWeight="semibold" noOfLines={2}>
        {task.taskTitle || task.description}
      </Text>
      {parentTitle && (
        <Text fontSize="xs" color="gray.500" noOfLines={1} mt={1}>
          Parent: {parentTitle}
        </Text>
      )}
      <Badge
        mt={2}
        size="sm"
        colorScheme={
          task.priority === 'urgent' ? 'red' : task.priority === 'high' ? 'orange' : 'gray'
        }
      >
        {task.priority}
      </Badge>
    </Box>
  );
};

interface KanbanColumnProps {
  status: string;
  tasks: Task[];
  canDrag: boolean;
  parentTitleById: Map<string, string>;
  onOpen: (taskId: string) => void;
}

const KanbanColumn: React.FC<KanbanColumnProps> = ({
  status,
  tasks,
  canDrag,
  parentTitleById,
  onOpen,
}) => {
  const columnBg = useColorModeValue('gray.50', 'gray.700');
  const dropHighlight = useColorModeValue('blue.50', 'blue.900');
  const { setNodeRef, isOver } = useDroppable({ id: columnId(status) });

  return (
    <Box
      ref={setNodeRef}
      bg={isOver ? dropHighlight : columnBg}
      borderRadius="xl"
      p={3}
      minH="200px"
      transition="background 0.15s ease"
    >
      <HStack justify="space-between" mb={3}>
        <Text fontWeight="bold" fontSize="sm">
          {status}
        </Text>
        <Badge>{tasks.length}</Badge>
      </HStack>
      <VStack align="stretch" spacing={2}>
        {tasks.map((task) => {
          const parentId =
            typeof task.parentTask === 'string' ? task.parentTask : undefined;
          return (
            <KanbanCard
              key={task._id}
              task={task}
              canDrag={canDrag}
              parentTitle={parentId ? parentTitleById.get(parentId) : undefined}
              onOpen={onOpen}
            />
          );
        })}
      </VStack>
    </Box>
  );
};

const TaskKanban: React.FC<TaskKanbanProps> = ({ tasks }) => {
  const navigate = useNavigate();
  const toast = useToast();
  const { authenticatedUser } = useAuthenticatedUser();
  const updateTaskMutation = useUpdateTask();
  const { data: organizationEnums } = useOrganizationEnums();
  const cardBg = useColorModeValue('white', 'gray.800');

  const canDrag = hasPermission(authenticatedUser, Permission.EDIT_TASKS);
  const [localTasks, setLocalTasks] = useState<Task[]>(tasks);
  const [activeTaskId, setActiveTaskId] = useState<string | null>(null);
  const suppressClickRef = useRef(false);

  useEffect(() => {
    setLocalTasks(tasks);
  }, [tasks]);

  const statuses = organizationEnums?.taskStatuses ?? [
    'Not Started',
    'In progress',
    'Completed',
    'On hold',
  ];

  const parentTitleById = useMemo(() => {
    const map = new Map<string, string>();
    localTasks.forEach((task) => {
      if (!task.isSubtask && !task.parentTask) {
        map.set(task._id, task.taskTitle || task.description);
      }
    });
    return map;
  }, [localTasks]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  );

  const byStatus = useMemo(() => {
    const map: Record<string, Task[]> = {};
    statuses.forEach((s) => {
      map[s] = [];
    });
    localTasks.forEach((t) => {
      const normalized = t.status.trim().toLowerCase();
      const key =
        statuses.find((s) => s.trim().toLowerCase() === normalized) ?? statuses[0];
      map[key].push(t);
    });
    return map;
  }, [localTasks, statuses]);

  const activeTask = activeTaskId
    ? localTasks.find((task) => task._id === activeTaskId)
    : null;

  const resolveTargetStatus = (overId: string): string | null => {
    const columnStatus = parseColumnId(String(overId));
    if (columnStatus) {
      return columnStatus;
    }

    const overTask = localTasks.find((task) => task._id === overId);
    return overTask?.status ?? null;
  };

  const handleDragStart = (event: DragStartEvent) => {
    suppressClickRef.current = false;
    setActiveTaskId(String(event.active.id));
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over, delta } = event;
    setActiveTaskId(null);

    if (Math.abs(delta.x) > 0 || Math.abs(delta.y) > 0) {
      suppressClickRef.current = true;
      window.setTimeout(() => {
        suppressClickRef.current = false;
      }, 0);
    }

    if (!over || !canDrag) {
      return;
    }

    const taskId = String(active.id);
    const newStatus = resolveTargetStatus(String(over.id));
    if (!newStatus) {
      return;
    }

    const task = localTasks.find((item) => item._id === taskId);
    if (!task || task.status === newStatus) {
      return;
    }

    const previousTasks = localTasks;
    setLocalTasks((current) =>
      current.map((item) => (item._id === taskId ? { ...item, status: newStatus } : item))
    );

    try {
      await updateTaskMutation.mutateAsync({
        id: taskId,
        data: { status: newStatus },
      });
      toast({
        title: 'Task updated',
        description: `Moved to ${newStatus}`,
        status: 'success',
        duration: 2000,
      });
    } catch (error: unknown) {
      setLocalTasks(previousTasks);
      const message = error instanceof Error ? error.message : 'Could not update task status';
      toast({
        title: 'Failed to move task',
        description: message,
        status: 'error',
        duration: 3000,
      });
    }
  };

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SimpleGrid columns={{ base: 1, md: 2, lg: statuses.length }} spacing={4} alignItems="start">
        {statuses.map((status) => (
          <KanbanColumn
            key={status}
            status={status}
            tasks={byStatus[status] ?? []}
            canDrag={canDrag}
            parentTitleById={parentTitleById}
            onOpen={(taskId) => {
              if (!suppressClickRef.current) {
                navigate(`/dashboard/task/${taskId}/details`);
              }
            }}
          />
        ))}
      </SimpleGrid>

      <DragOverlay>
        {activeTask ? (
          <Box
            bg={cardBg}
            p={3}
            borderRadius="lg"
            boxShadow="lg"
            cursor="grabbing"
            w="260px"
          >
            <Text fontSize="sm" fontWeight="semibold" noOfLines={2}>
              {activeTask.taskTitle || activeTask.description}
            </Text>
            <Badge
              mt={2}
              size="sm"
              colorScheme={
                activeTask.priority === 'urgent'
                  ? 'red'
                  : activeTask.priority === 'high'
                    ? 'orange'
                    : 'gray'
              }
            >
              {activeTask.priority}
            </Badge>
          </Box>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default TaskKanban;
